"use client";

import { DropdownMenuTrigger } from "@ui/components/dropdown-menu";
import { cn } from "@ui/lib";
import {
	ChevronDownIcon,
	PlusIcon,
	RefreshCwIcon,
	UploadIcon,
	XIcon,
} from "lucide-react";
import { type DragEvent, type ReactNode, useRef, useState } from "react";

/**
 * 首页 hero 输入卡的几个小控件（参数胶囊、上传格、结果占位），
 * /ai-image-generator 的生图 hero 也用，样式两边保持一致。
 */

/** hero 锚点：底部 CTA 滚回这里并聚焦提示词框（视频 hero 和生图 hero 都挂这两个 id） */
export const VIDEO_HERO_ID = "video-generator";
export const VIDEO_HERO_PROMPT_ID = "video-generator-prompt";
/** 页面其他位置（模式卡的 Try it）切换 hero tab 用，detail 是 HeroMode */
export const VIDEO_HERO_MODE_EVENT = "video-hero:set-mode";
/** 预设页样片的「Try this example」把原图按槽位顺序填进 hero，detail 是图片 URL 数组 */
export const VIDEO_HERO_IMAGES_EVENT = "video-hero:set-images";
/** 预设页样片的「Try this example」顺带切模板（Rumpelstiltskin 的宠物样片），detail 是模板 id */
export const VIDEO_HERO_TEMPLATE_EVENT = "video-hero:set-template";
export const VIDEO_HERO_EXAMPLE_EVENT = "video-hero:set-example";
/** /ai-image-generator 样片区和 Prompt 样片墙的「Try this prompt」回填生图 hero，detail 是 ImageHeroExample */
export const IMAGE_HERO_EXAMPLE_EVENT = "image-hero:set-example";

export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

export function Chip({ label, icon }: { label: string; icon?: ReactNode }) {
	return (
		<DropdownMenuTrigger asChild>
			<button
				type="button"
				className="flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-gray-4 px-3 font-ui text-gray-11 text-xs transition-colors hover:bg-gray-5 data-[state=open]:bg-gray-5 sm:px-3.5"
			>
				{icon}
				{label}
				<ChevronDownIcon className="size-3.5 text-gray-9" />
			</button>
		</DropdownMenuTrigger>
	);
}

export function ResultPlaceholder({ children }: { children: ReactNode }) {
	return (
		<div className="flex aspect-video flex-col items-center justify-center gap-3 rounded-2xl bg-gray-2 p-6 text-center text-ink-3 text-sm">
			{children}
		</div>
	);
}

export function UploadBox({
	previewUrl,
	label,
	removeLabel,
	multiple = false,
	compact = false,
	wide = false,
	fill = false,
	short = false,
	highlight = false,
	accent = false,
	swapLabel,
	locked = false,
	accept = ACCEPTED_IMAGE_TYPES.join(","),
	banner = false,
	hint,
	iconOnly = false,
	onFiles,
	onRemove,
}: {
	previewUrl: string | null;
	label: string;
	removeLabel: string;
	/** 参考图的加号格一次可选多张；单槽只取第一张 */
	multiple?: boolean;
	/** 参考图最多 7 张，缩小成一行三格，别把输入卡撑到四行高 */
	compact?: boolean;
	/** 手机上整宽矮条（文生态的可选上传），sm 起回到方块 */
	wide?: boolean;
	/** 手机上两格（首尾帧、预设双槽）平分整行、80 高的横条，别只占左边一截；sm 起回到方块 */
	fill?: boolean;
	/** 预设页（Hotel Lobby）的人像格再矮一档，给下面的分段控件和 Create 让位 */
	short?: boolean;
	/** 图生模板等用户传图时描边高亮 */
	highlight?: boolean;
	/** 品牌色虚线、不闪：常驻的「这里要传图」提示（生图 hero 的图生图 tab），highlight 会一直 pulse 太晃 */
	accent?: boolean;
	/** 槽里是预设的默认图：整格点开文件选择（拖图进来也行）换一张，底部显示这段文案，不出删除 */
	swapLabel?: string;
	/** 槽里是锁死的默认图：只展示图和槽位名，不能换也不能删 */
	locked?: boolean;
	/** 参考格在收视频的页面上额外收视频类型 */
	accept?: string;
	/** 参考格一个文件都没有时整条宽格，文案一行放下「Image / Video / Audio」 */
	banner?: boolean;
	/** banner 下面那行小字 */
	hint?: string;
	/** 参考格传了文件后的加号格，只出 + 图标，label 当无障碍名 */
	iconOnly?: boolean;
	onFiles: (files: File[]) => void;
	onRemove: () => void;
}) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [isDragOver, setIsDragOver] = useState(false);

	const fileInput = (
		<input
			ref={inputRef}
			type="file"
			accept={accept}
			multiple={multiple}
			className="hidden"
			onChange={(event) => {
				onFiles(Array.from(event.target.files ?? []));
				event.target.value = "";
			}}
		/>
	);
	const dropHandlers = {
		onDragOver: (event: DragEvent<HTMLButtonElement>) => {
			event.preventDefault();
			setIsDragOver(true);
		},
		onDragLeave: () => setIsDragOver(false),
		onDrop: (event: DragEvent<HTMLButtonElement>) => {
			event.preventDefault();
			setIsDragOver(false);
			const files = Array.from(event.dataTransfer.files);
			onFiles(multiple ? files : files.slice(0, 1));
		},
	};

	if (previewUrl && (swapLabel || locked)) {
		const frameClassName = cn(
			"group relative w-full overflow-hidden rounded-2xl bg-gray-2 ring-1 ring-black/[.04]",
			fill ? (short ? "h-16 sm:h-32" : "h-20 sm:h-32") : "h-28 sm:h-32",
			isDragOver && "ring-2 ring-brand",
		);
		const image = (
			<img
				src={previewUrl}
				alt={label}
				className={cn("size-full object-cover", short && "object-top")}
			/>
		);
		// 默认图下面挂一行槽位名，不然看不出这张图是干嘛的
		return (
			<div
				className={cn(
					"flex shrink-0 flex-col gap-1",
					fill
						? "min-w-0 flex-1 sm:w-32 sm:flex-none"
						: "w-28 sm:w-32",
				)}
			>
				{locked ? (
					<div className={frameClassName}>{image}</div>
				) : (
					<button
						type="button"
						onClick={() => inputRef.current?.click()}
						{...dropHandlers}
						aria-label={`${swapLabel}: ${label}`}
						className={frameClassName}
					>
						{image}
						{/* 手机上格子只有 64 高，药丸压薄到 20 高，少挡人像 */}
						<span className="absolute inset-x-1 bottom-1 flex items-center justify-center gap-1 rounded-full bg-black/60 py-0.5 font-medium text-white text-xs group-hover:bg-black/80">
							<RefreshCwIcon className="size-3" />
							{swapLabel}
						</span>
					</button>
				)}
				<span
					aria-hidden
					className="text-balance text-center text-[11px] text-gray-9 leading-tight"
				>
					{label}
				</span>
				{fileInput}
			</div>
		);
	}

	if (previewUrl) {
		return (
			<div
				className={cn(
					"relative shrink-0 overflow-hidden rounded-2xl bg-gray-2 ring-1 ring-black/[.04]",
					compact
						? "size-20"
						: fill
							? cn(
									short ? "h-16" : "h-20",
									"min-w-0 flex-1 sm:size-32 sm:flex-none",
								)
							: "size-28 sm:size-32",
				)}
			>
				<img
					src={previewUrl}
					alt={label}
					// 预设页的矮人像格按中心裁只剩躯干，贴顶裁才看得到脸
					className={cn(
						"size-full object-cover",
						short && "object-top",
					)}
				/>
				<button
					type="button"
					onClick={onRemove}
					aria-label={removeLabel}
					className={cn(
						"absolute top-1.5 right-1.5 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80",
						compact ? "size-6" : "size-7",
					)}
				>
					<XIcon className={compact ? "size-3.5" : "size-4"} />
				</button>
			</div>
		);
	}

	return (
		<>
			<button
				type="button"
				onClick={() => inputRef.current?.click()}
				{...dropHandlers}
				aria-label={iconOnly ? label : undefined}
				className={cn(
					"flex shrink-0 cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed bg-gray-2 font-medium text-gray-9 text-xs transition-colors",
					banner
						? "h-24 w-full gap-1 text-sm sm:h-28"
						: compact
							? "size-20 gap-1 text-[11px]"
							: wide
								? "h-12 w-full flex-row sm:size-32 sm:flex-col"
								: fill
									? cn(
											short ? "h-16" : "h-20",
											"min-w-0 flex-1 sm:size-32 sm:flex-none",
										)
									: "size-28 sm:size-32",
					isDragOver || highlight || accent
						? "border-brand bg-brand-a08 text-brand"
						: "border-gray-5 hover:border-brand-300 hover:bg-brand-50/50 hover:text-brand",
					highlight && !isDragOver && "animate-pulse",
				)}
			>
				{iconOnly ? (
					<PlusIcon className="size-6" />
				) : (
					<>
						<UploadIcon className={compact ? "size-5" : "size-6"} />
						{/* 长标签（丧尸页 "The undead loved one"）在 128 宽的格里会顶到虚线框，留边并允许换行 */}
						<span className="text-balance px-2 text-center leading-tight">
							{label}
						</span>
						{hint ? (
							<span className="font-normal text-[11px] text-gray-8">
								{hint}
							</span>
						) : null}
					</>
				)}
			</button>
			{fileInput}
		</>
	);
}
